'use client';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { DataGridPremium, GridColDef, GridRowParams } from '@mui/x-data-grid-premium';
import { useLazyQuery, useMutation } from '@parkchirp/api-client';
import React, { BaseSyntheticEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { GET_ACH_DISCOUNT_ELIGIBILITY, GET_CREDIT_CARD_FEE_PRICE, PAY_INVOICE_PROCESS_PAYMENT } from '../../queries';
import { ProcessPaymentProps } from '../../types';
import { convertUnixMsToDateInTimezone, datagridFilterPanelAttributes, formatCurrency } from '../../utils';
import { CurrencyInput, CurrencyInputChangeEvent } from '../inputs';
import { AchDiscountNotice } from './achDiscountNotice';
import { CreditCardFeeDisclaimer } from './creditCardFeeDisclaimer';
import { GPICreditCardForm } from './gpi/gpiCreditCardForm';

interface SingleInvoice {
	id: string;
	readable_id: string;
	created_at: string;
	amount: number;
	balance: number;
	payment: string | number;
	checkbox: boolean;
}

// Copied over from admin dashboard for re-use
// Should probably refactor this when time permits
// TODO: this component could use some big refactoring
export const ProcessPayment: FC<ProcessPaymentProps> = ({
	useAch,
	useCreditCard,
	adminProcessPayments,
	editPaymentOptions,
	billingProfileId,
	cognitoSub,
	subscriptionId,
	profileData,
	migratePayorComponent,
	setShowSnackbar,
	gpiFormConfig,
	invoiceData,
	invoiceLoading,
	refetchInvoices,
	tenantSubscriptionId,
	appName,
}) => {
	const [checkbox, setCheckbox] = useState<string[]>([]);
	const [selectedToken, setSelectedToken] = useState<string | null>(null);
	const [addCard, setAddCard] = useState<boolean>(false);
	const [invoiceRows, setInvoiceRows] = useState<SingleInvoice[]>([]);
	const [saveNewCard, setSaveNewCard] = useState(false);
	const [disableSubmitButton, setDisableSubmitButton] = useState(false);
	const [isEligibleForAchDiscount, setIsEligibleForAchDiscount] = useState(false);
	const [achDiscountPercentage, setAchDiscountPercentage] = useState(0);
	const [achDiscountAmount, setAchDiscountAmount] = useState(0);
	const [creditCardFeePrice, setCreditCardFeePrice] = useState(0);
	const [creditCardFeeDisplayPrice, setCreditCardFeeDisplayPrice] = useState('');

	const cards = profileData?.gpiCards;
	const bankAccounts = profileData?.gpiBankAccounts;

	const disableEdit = adminProcessPayments === 'DISPLAY_AND_VIEW_ONLY';
	const disableAddCard = editPaymentOptions === 'DISPLAY_AND_VIEW_ONLY';

	const temporaryTokenRef = useRef<string | null>(null);
	const setTemporaryToken = useCallback((token: string | null) => {
		temporaryTokenRef.current = token;
	}, []);

	useEffect(() => {
		if (invoiceData) {
			const fetchedInvoiceRows: SingleInvoice[] = invoiceData.map((invoice: any) => ({
				id: invoice.id,
				readable_id: invoice.readable_id || invoice.readableId,
				created_at: convertUnixMsToDateInTimezone(invoice.created_at || invoice.createdAt),
				amount: invoice.amount / 100,
				balance: invoice.balance / 100,
				payment: (invoice.balance / 100).toFixed(2),
				checkbox: !disableEdit,
			}));

			setInvoiceRows(fetchedInvoiceRows);

			if (!disableEdit) {
				const allFetchedRowIds = fetchedInvoiceRows.map((row) => row.id);
				setCheckbox(allFetchedRowIds);
			}
		}
	}, [invoiceData, disableEdit]);

	const [payInvoice, { loading: payInvoiceLoading }] = useMutation(PAY_INVOICE_PROCESS_PAYMENT, {
		onCompleted: () => {
			setShowSnackbar({
				message: 'The Payment has been processed.',
				severity: 'success',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			setAddCard(false);
		},
	});

	const handleSelectionChange = useCallback((token: string | null) => {
		setSelectedToken((prevToken) => (prevToken === token ? null : token));
	}, []);

	const handleAddCardToggle = useCallback(() => {
		setSelectedToken(null);
		setAddCard((prev) => !prev);
		if (addCard) {
			setSelectedToken(null);
		}
	}, [addCard]);

	const generatePaymentVariables = useCallback(
		(invoice: any, tokenToUse: string | null) => {
			const paymentAmount = Math.round(Number(invoice.payment) * 100);
			const isFullPayment = Math.round(invoice.balance * 100) === paymentAmount;
			const paymentToken = tokenToUse || selectedToken;

			const cardTokens = cards.map(({ token }) => token);
			const bankTokens = bankAccounts.map(({ token }) => token);
			const paymentMethod =
				!selectedToken || cardTokens.includes(paymentToken)
					? 'card'
					: bankTokens.includes(paymentToken)
						? 'ach'
						: 'invalidType';

			return {
				invoiceId: invoice.id,
				paymentToken,
				paymentMethod,
				allowDuplicate: true,
				...(!isFullPayment && { customPaymentAmount: paymentAmount }),
			};
		},
		[selectedToken, bankAccounts, cards],
	);

	const handlePaymentSubmit = useCallback(
		async (event: BaseSyntheticEvent, tokenToUse: string | null) => {
			if (event && event.preventDefault) {
				event.preventDefault();
			}
			if (payInvoiceLoading || invoiceLoading || disableSubmitButton) {
				return;
			}

			const selectedInvoices = invoiceRows.filter((row: any) => checkbox.includes(row.id));
			if (!selectedInvoices.length) return;

			setDisableSubmitButton(true);
			const isValid = selectedInvoices.every(
				(invoice: any) => Number(invoice.payment) >= 0 && Number(invoice.payment) <= Number(invoice.balance),
			);

			if (isValid) {
				const paymentPromises = selectedInvoices.map((invoice) => {
					const paymentVariables = generatePaymentVariables(invoice, tokenToUse);
					return payInvoice({
						variables: {
							...paymentVariables,
							tenantSubscriptionId,
						},
						context: {
							apiVersion: 'v2',
							appName,
						},
					});
				});

				try {
					await Promise.all(paymentPromises);
					setTemporaryToken(null);
					temporaryTokenRef.current = null;
				} catch (error: any) {
					setShowSnackbar({
						message: error.message,
						severity: 'error',
						autoHideDuration: 3000,
						onClose: () => {
							setShowSnackbar(null);
						},
					});
				} finally {
					refetchInvoices();
				}
			} else {
				setShowSnackbar({
					message: 'Invalid payment amount',
					severity: 'error',
					autoHideDuration: 5000,
					onClose: () => {
						setShowSnackbar(null);
					},
				});
			}
			setDisableSubmitButton(false);
		},
		[
			payInvoice,
			setShowSnackbar,
			checkbox,
			invoiceRows,
			refetchInvoices,
			setTemporaryToken,
			generatePaymentVariables,
			tenantSubscriptionId,
			payInvoiceLoading,
			invoiceLoading,
			disableSubmitButton,
			appName,
		],
	);

	const getTotalRowClassName = useCallback((params: GridRowParams) => {
		if (params.id === 'total') {
			return 'total-row';
		}
		return '';
	}, []);

	const creditCardFee = useMemo(() => {
		if (creditCardFeePrice > 0) {
			return Number(creditCardFeeDisplayPrice);
		}

		return null;
	}, [creditCardFeePrice, creditCardFeeDisplayPrice]);

	const showCreditCardFeeDisclaimer = useMemo(() => {
		if (!cards) {
			return false;
		}

		if (checkbox.length === 0) {
			return false;
		}

		if (!cards.some((card) => card.token === selectedToken)) {
			return false;
		}

		return true;
	}, [cards, checkbox, selectedToken]);

	const showAchDiscountNotice = useMemo(() => {
		if (checkbox.length === 0) {
			return false;
		}

		if (!bankAccounts?.some((account) => account.token === selectedToken)) {
			return false;
		}

		if (!invoiceRows.some((row) => row.checkbox && row.payment === row.balance.toFixed(2))) {
			return false;
		}

		return true;
	}, [bankAccounts, invoiceRows, selectedToken, checkbox]);

	const totalPaymentAmount = useMemo(() => {
		const paymentAmount = invoiceRows
			.filter((row) => checkbox.includes(row.id))
			.map((row) => Number(row.payment))
			.reduce((a, b) => a + b, 0);

		if (showCreditCardFeeDisclaimer) {
			const partialPaymentAmounts = invoiceRows
				.filter((row) => row.checkbox && row.balance !== Number(row.payment))
				.map((row) => Number(row.payment))
				.reduce((a, b) => a + b, 0);

			if (partialPaymentAmounts > 0) {
				const adjustedCreditCardFee = creditCardFee - creditCardFee * (partialPaymentAmounts / paymentAmount);
				return (paymentAmount + adjustedCreditCardFee).toFixed(2);
			}

			return (paymentAmount + creditCardFee).toFixed(2);
		}

		if (showAchDiscountNotice) {
			return (paymentAmount - achDiscountAmount).toFixed(2);
		}

		return paymentAmount.toFixed(2);
	}, [achDiscountAmount, checkbox, creditCardFee, invoiceRows, showAchDiscountNotice, showCreditCardFeeDisclaimer]);

	const { totalAmount, totalBalance, totalPayment } = useMemo(() => {
		const amount = invoiceRows.reduce((sum, row) => sum + Number(row.amount || '0'), 0);
		const balance = invoiceRows.reduce((sum, row) => sum + Number(row.balance || '0'), 0);
		const payment = invoiceRows.reduce((sum, row) => sum + Number(row.payment || '0'), 0);

		return { totalAmount: amount, totalBalance: balance, totalPayment: payment };
	}, [invoiceRows]);

	const [getCreditCardFeePrice, { loading: creditCardFeeLoading, error: creditCardFeeError }] = useLazyQuery(
		GET_CREDIT_CARD_FEE_PRICE,
		{
			context: { apiVersion: 'v2' },
			fetchPolicy: 'network-only',
			onCompleted: ({ getCreditCardFeePrice: response }) => {
				setCreditCardFeePrice(response.price / 100);
				setCreditCardFeeDisplayPrice(response.displayPrice);
			},
		},
	);

	const [getAchDiscountEligibility, { loading: achDiscountEligibilityLoading }] = useLazyQuery(
		GET_ACH_DISCOUNT_ELIGIBILITY,
		{
			context: { apiVersion: 'v2' },
			onCompleted: ({ getAchDiscountEligibility: response }) => {
				const { isEligible, discountPercentage, totalDiscountAmount } = response;

				setIsEligibleForAchDiscount(isEligible);
				setAchDiscountPercentage(discountPercentage);
				setAchDiscountAmount(totalDiscountAmount / 100);
			},
		},
	);

	useEffect(() => {
		if (billingProfileId && !invoiceLoading && checkbox.length > 0) {
			getCreditCardFeePrice({
				variables: {
					billingProfileId,
					paymentAmount: Math.round(totalPayment * 100),
					numberOfInvoices: checkbox.length,
					subscriptionId,
					tenantSubscriptionId,
				},
				context: {
					apiVersion: 'v2',
				},
				fetchPolicy: 'network-only',
			});

			const selectedInvoiceIds = invoiceRows.filter((row) => checkbox.includes(row.id)).map((row) => row.id);

			getAchDiscountEligibility({
				variables: {
					subscriptionId,
					invoiceIds: selectedInvoiceIds,
				},
			});
		}
	}, [
		billingProfileId,
		checkbox,
		invoiceRows,
		getAchDiscountEligibility,
		getCreditCardFeePrice,
		subscriptionId,
		totalPayment,
		invoiceLoading,
		tenantSubscriptionId,
	]);

	useEffect(() => {
		if (addCard) {
			setCheckbox([]);
			setInvoiceRows((rows: any) => rows.map((row: any) => ({ ...row, checkbox: false, payment: 0.0 })));
		}
	}, [addCard]);

	const handleCheckbox = useCallback(
		(event: any, rowId: string) => {
			const updatedValue = event.target.checked;

			if (addCard) {
				if (updatedValue) {
					setCheckbox([rowId]);
					setInvoiceRows((rows) =>
						rows.map((row) => {
							if (row.id === rowId) {
								return { ...row, checkbox: true, payment: row.balance.toFixed(2) };
							}
							return { ...row, checkbox: false, payment: 0.0 };
						}),
					);
				} else {
					setCheckbox([]);
					setInvoiceRows((rows) => rows.map((row) => ({ ...row, checkbox: false, payment: 0.0 })));
				}
			} else {
				if (updatedValue) {
					setCheckbox((prev) => [...prev, rowId]);
				} else {
					setCheckbox((prev) => prev.filter((id) => id !== rowId));
				}

				const rowIndex = invoiceRows.findIndex((row) => row.id === rowId);
				if (rowIndex > -1) {
					invoiceRows[rowIndex].checkbox = updatedValue;
					invoiceRows[rowIndex].payment = updatedValue ? invoiceRows[rowIndex].balance.toFixed(2) : 0.0;
				}
				setInvoiceRows([...invoiceRows]);
			}
		},
		[invoiceRows, addCard],
	);

	const handleHeaderCheckbox = useCallback(
		(event: BaseSyntheticEvent) => {
			if (addCard) {
				return;
			}
			if (event.target.checked) {
				const newSelectedIds = invoiceRows.map((row) => row.id);
				setCheckbox(newSelectedIds);
				setInvoiceRows((rows) =>
					rows.map((row) => ({ ...row, checkbox: true, payment: row.balance.toFixed(2) })),
				);
			} else {
				setCheckbox([]);
				setInvoiceRows((rows) => rows.map((row) => ({ ...row, checkbox: false, payment: 0.0 })));
			}
		},
		[invoiceRows, addCard],
	);

	const handlePaymentAmountChange = useCallback(
		(event: CurrencyInputChangeEvent, rowId: string) => {
			const inputValue = event.target.value;

			const rowIndex = invoiceRows.findIndex((row) => row.id === rowId);

			if (rowIndex > -1) {
				invoiceRows[rowIndex].payment = inputValue;
				invoiceRows[rowIndex].checkbox = inputValue !== '' && inputValue !== '0.00';
			}

			setInvoiceRows([...invoiceRows]);

			if (inputValue !== '' && inputValue !== '0.00' && !checkbox.includes(rowId)) {
				setCheckbox((prevCheckbox) => [...prevCheckbox, rowId]);
			} else if ((inputValue === '' || inputValue === '0.00') && checkbox.includes(rowId)) {
				setCheckbox((prevCheckbox) => prevCheckbox.filter((id) => id !== rowId));
			}
		},
		[invoiceRows, checkbox],
	);

	const noInvoice = !invoiceLoading && invoiceRows.length === 0;

	const totalRow = useMemo(
		() => ({
			id: 'total',
			readable_id: 'Total',
			created_at: '',
			amount: totalAmount,
			balance: totalBalance,
			payment: totalPaymentAmount,
		}),
		[totalAmount, totalBalance, totalPaymentAmount],
	);

	const checkboxColumn: GridColDef = {
		field: 'checkbox',
		headerName: '',
		width: 50,
		editable: false,
		resizable: false,
		sortable: false,
		headerAlign: 'center',
		renderHeader: () => (
			<Checkbox
				size='small'
				indeterminate={checkbox.length > 0 && checkbox.length < invoiceRows.length}
				checked={checkbox.length === invoiceRows.length && invoiceRows.length > 0}
				onChange={handleHeaderCheckbox}
				data-cy='invoice-row-checkbox'
			/>
		),
		renderCell: (params) => {
			if (params.id === 'total') {
				return null;
			}
			return <Checkbox size='small' checked={params.value} onChange={(e) => handleCheckbox(e, params.row.id)} />;
		},
	};

	const paymentAmountColumn: GridColDef = {
		field: 'payment',
		headerName: 'Payment Amount',
		width: 100,
		flex: 1,
		editable: false,
		resizable: false,
		sortable: false,
		headerAlign: 'center',
		cellClassName: 'tableCell',
		renderCell: (params) => {
			if (params.id === 'total') {
				return formatCurrency(params.value);
			}

			return (
				<CurrencyInput
					value={params.value}
					onChange={(e) => handlePaymentAmountChange(e, params.row.id)}
					onBlur={(e) => handlePaymentAmountChange(e, params.row.id)}
				/>
			);
		},
	};

	const viewOnlyColumns: GridColDef[] = [
		{
			field: 'readable_id',
			headerName: 'Invoice #',
			width: 350,
			flex: 2,
			renderCell: (params) => params.value,
			editable: false,
			resizable: false,
			sortable: false,
			headerAlign: 'center',
			cellClassName: (params) => (params.id !== 'total' ? 'tableCell' : 'total-row'),
		},
		{
			field: 'created_at',
			headerName: 'Invoice Date',
			width: 120,
			flex: 1,
			editable: false,
			resizable: false,
			sortable: false,
			headerAlign: 'center',
			cellClassName: 'tableCell',
		},
		{
			field: 'amount',
			headerName: 'Invoice Amount',
			renderCell: (params) => formatCurrency(params.value),
			width: 100,
			flex: 1,
			editable: false,
			resizable: false,
			sortable: false,
			headerAlign: 'center',
			cellClassName: 'tableCell',
		},
		{
			field: 'balance',
			headerName: 'Invoice Balance',
			renderCell: (params) => formatCurrency(params.value),
			width: 100,
			flex: 1,
			editable: false,
			resizable: false,
			sortable: false,
			headerAlign: 'center',
			cellClassName: 'tableCell',
		},
	];

	let invoiceColumns: GridColDef[] = [];

	invoiceColumns = !disableEdit
		? [...invoiceColumns, checkboxColumn, ...viewOnlyColumns, paymentAmountColumn]
		: [...viewOnlyColumns];

	return (
		<Box className='pca___subscription-process-payment' sx={{ padding: 3, marginTop: -1 }}>
			{noInvoice && (
				<Alert severity='info' data-cy='alert-message' sx={{ color: 'black' }}>
					{`All of this subscription's invoices have been paid`}
				</Alert>
			)}
			<DataGridPremium
				className='invoice-table'
				columns={invoiceColumns}
				rows={[...invoiceRows, totalRow]}
				loading={invoiceLoading}
				getRowClassName={getTotalRowClassName}
				getRowHeight={(params) => (params.id === 'total' ? 50 : 40)}
				autoHeight
				disableRowSelectionOnClick
				disableColumnReorder
				disableColumnMenu
				rowSelectionModel={checkbox}
				onRowSelectionModelChange={handleCheckbox as any}
				hideFooter
				slotProps={{ filterPanel: { ...datagridFilterPanelAttributes } }}
			/>
			{!disableEdit && (
				<Box sx={{ mt: 5 }}>
					<h3>Payment Options</h3>
					{!cognitoSub ? <>{migratePayorComponent}</> : null}

					{!useCreditCard && !useAch ? (
						<Alert severity='info' sx={{ mt: 1 }}>
							Online payments are not available for this subscription. Please contact an admin for help
							with payment options.
						</Alert>
					) : (
						<Grid container spacing={2} sx={{ mt: 1 }}>
							{useCreditCard && (
								<Grid item xs={5} sm={6}>
									<h4>Credit Card</h4>
									<Box
										sx={{
											mt: 1,
											mb: 1,
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'flex-start',
											width: '30%',
										}}
									>
										{cards?.map((card) => {
											const expMonth = card.exp_month || card.expMonth;
											const expYear = card.exp_year || card.expYear;
											return (
												<FormControlLabel
													key={card.token}
													control={
														<Checkbox
															size='small'
															checked={selectedToken === card.token}
															onChange={() => handleSelectionChange(card.token)}
															disabled={addCard}
															data-cy='credit-card-checkbox'
														/>
													}
													label={
														<Typography variant='body2'>
															{`${card.type} ending in ${card.last4}, ${expMonth}/${expYear}`}
														</Typography>
													}
												/>
											);
										})}

										{!disableAddCard && (
											<Button
												variant='outlined'
												size='small'
												sx={{ mt: 3, mb: 3 }}
												onClick={handleAddCardToggle}
												data-cy='use-existing-card-toggle-button'
											>
												{addCard ? 'Use an existing card' : 'Use a different card'}
											</Button>
										)}
									</Box>
								</Grid>
							)}

							{useAch && (
								<Grid item xs={5} sm={6}>
									<h4>Bank Account</h4>
									<Box
										sx={{
											mt: 1,
											mb: 1,
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'flex-start',
											width: '30%',
										}}
									>
										{bankAccounts?.map((account) => (
											<FormControlLabel
												key={account.token}
												control={
													<Checkbox
														size='small'
														checked={selectedToken === account.token}
														onChange={() => handleSelectionChange(account.token)}
														disabled={addCard}
														data-cy='bank-account-checkbox'
													/>
												}
												label={
													<Typography variant='body2'>
														{`${account.accountType} account ending in ${account.last4}`}
													</Typography>
												}
											/>
										))}

										{showAchDiscountNotice && (
											<AchDiscountNotice
												isEligible={isEligibleForAchDiscount}
												isLoading={achDiscountEligibilityLoading}
												discountPercentage={achDiscountPercentage}
											/>
										)}
									</Box>
								</Grid>
							)}
						</Grid>
					)}

					{showCreditCardFeeDisclaimer && (
						<CreditCardFeeDisclaimer
							creditCardFee={creditCardFee?.toFixed(2)}
							creditCardFeeLoading={creditCardFeeLoading}
							creditCardFeeError={creditCardFeeError}
						/>
					)}

					{addCard && (
						<Alert severity='error' sx={{ mt: 3, mb: 3, width: 'auto' }} data-cy='select-invoice-alert'>
							Please select an invoice, only one invoice can be paid.
						</Alert>
					)}

					{!addCard && (
						<Button
							size='small'
							variant='contained'
							sx={{ mt: 3 }}
							onClick={(event) => handlePaymentSubmit(event, temporaryTokenRef.current)}
							disabled={
								payInvoiceLoading ||
								creditCardFeeLoading ||
								(!selectedToken && !temporaryTokenRef.current)
							}
							data-cy='make-payment-button'
						>
							{payInvoiceLoading ? (
								<CircularProgress size={20} sx={{ color: 'white' }} data-cy='loading-indicator' />
							) : (
								'Make Payment'
							)}
						</Button>
					)}
				</Box>
			)}

			{!disableEdit && addCard && (
				<Box>
					<GPICreditCardForm
						cognitoSub={cognitoSub}
						setTemporaryToken={setTemporaryToken}
						isProcessPayment
						saveNewCard={saveNewCard}
						onSubmit={(event) => handlePaymentSubmit(event, temporaryTokenRef.current)}
						payInvoiceLoading={payInvoiceLoading}
						setShowSnackbar={setShowSnackbar}
						gpiFormConfig={gpiFormConfig}
						tenantSubscriptionId={tenantSubscriptionId}
					/>
					<FormControlLabel
						control={
							<Checkbox
								size='small'
								onChange={() => setSaveNewCard(!saveNewCard)}
								data-cy='save-card-checkbox'
							/>
						}
						label={<Typography variant='body2'>Save card for future use</Typography>}
					/>
				</Box>
			)}

			{!disableEdit && addCard && (
				<Box>
					<GPICreditCardForm
						cognitoSub={cognitoSub}
						setTemporaryToken={setTemporaryToken}
						isProcessPayment
						saveNewCard={saveNewCard}
						onSubmit={(event) => handlePaymentSubmit(event, temporaryTokenRef.current)}
						payInvoiceLoading={payInvoiceLoading}
						setShowSnackbar={setShowSnackbar}
						gpiFormConfig={gpiFormConfig}
						tenantSubscriptionId={tenantSubscriptionId}
					/>
					<FormControlLabel
						control={
							<Checkbox
								size='small'
								onChange={() => setSaveNewCard(!saveNewCard)}
								data-cy='save-card-checkbox'
							/>
						}
						label={<Typography variant='body2'>Save card for future use</Typography>}
					/>
				</Box>
			)}
		</Box>
	);
};
